import { useMemo, useState, type ChangeEvent, type ReactElement } from 'react';
import type { RouteAnalyticsView, RouteSeatShareView } from '../../../domain/models/contracts';
import { RouteDetailModal } from './RouteDetailModal';
import {
  ROUTE_SORT_LABELS,
  rankRoutes,
  summarizeRoutes,
  type RouteSortKey,
} from './routeAggregates';
import { formatCents, formatInt, formatPct, formatUsd } from './formatters';
import './RouteAnalyticsPanel.css';

export interface RouteAnalyticsPanelProps {
  /** Latest per-route analytics from the worker's TICK_DAILY event. */
  readonly analytics: readonly RouteAnalyticsView[];
  /** Published seat shares per O&D market, for the competitor comparison. */
  readonly competitorShares: readonly RouteSeatShareView[];
  /** Opens a route in the Route inspector, where its fares can be edited. */
  readonly onOpenInspector: (legId: string) => void;
}

const SORT_KEYS: readonly RouteSortKey[] = ['PROFIT', 'REVENUE', 'LOAD_FACTOR', 'YIELD'];

/**
 * Default Route Analytics view: a network aggregate over every published route,
 * with a ranked table. Clicking a row opens the route detail modal.
 */
export function RouteAnalyticsPanel({
  analytics,
  competitorShares,
  onOpenInspector,
}: RouteAnalyticsPanelProps): ReactElement {
  const [sortKey, setSortKey] = useState<RouteSortKey>('PROFIT');
  const [openLegId, setOpenLegId] = useState<string | null>(null);

  const summary = useMemo(() => summarizeRoutes(analytics), [analytics]);
  const ranked = useMemo(() => rankRoutes(analytics, sortKey), [analytics, sortKey]);
  const openRoute = openLegId === null ? undefined : analytics.find((route) => route.legId === openLegId);

  if (analytics.length === 0) {
    return (
      <div className="rap rap--empty">
        <h2 className="rap-title">Network overview</h2>
        <p className="rap-empty">
          No published routes yet. Create a flight leg and run the simulation for a day to see analytics.
        </p>
      </div>
    );
  }

  const handleSortChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const next = SORT_KEYS.find((key) => key === event.target.value);
    if (next !== undefined) setSortKey(next);
  };

  return (
    <div className="rap">
      <header className="rap-header">
        <div>
          <h2 className="rap-title">Network overview</h2>
          <p className="rap-sub">
            Most recent daily close · {formatInt(summary.routeCount)} published routes · select a route for leg detail
          </p>
        </div>
        <label className="rap-sort">
          <span>Rank by</span>
          <select className="rap-sort__select" value={sortKey} onChange={handleSortChange} aria-label="Rank routes by">
            {SORT_KEYS.map((key) => (
              <option key={key} value={key}>
                {ROUTE_SORT_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
      </header>

      <div className="rap-kpis">
        <div className="rap-kpi">
          <span className="rap-kpi__label">Fleet revenue</span>
          <strong className="rap-kpi__value">{formatUsd(summary.revenueUSD)}</strong>
          <p className="rap-kpi__sub">
            Cost {formatUsd(summary.operatingCostUSD)} · profit{' '}
            <span className={summary.profitUSD >= 0 ? 'pos' : 'neg'}>{formatUsd(summary.profitUSD)}</span>
          </p>
        </div>
        <div className="rap-kpi">
          <span className="rap-kpi__label">Network load factor</span>
          <strong className="rap-kpi__value">{formatPct(summary.loadFactorPct)}</strong>
          <p className="rap-kpi__sub">
            {formatInt(summary.passengers)} passengers on {formatInt(summary.seats)} seats
          </p>
        </div>
        <div className="rap-kpi">
          <span className="rap-kpi__label">RPK</span>
          <strong className="rap-kpi__value">{formatInt(summary.revenuePassengerMiles)}</strong>
          <p className="rap-kpi__sub">
            Revenue passenger-miles, derived · yield {formatCents(summary.yieldCentsPerRpm)}
          </p>
        </div>
        <div className="rap-kpi">
          <span className="rap-kpi__label">ASK</span>
          <strong className="rap-kpi__value">{formatInt(summary.availableSeatMiles)}</strong>
          <p className="rap-kpi__sub">Available seat-miles · {formatInt(summary.operatedFlights)} flights operated</p>
        </div>
      </div>

      <div className="rap-table-wrap">
        <table className="rap-table">
          <thead>
            <tr>
              <th scope="col" className="num">#</th>
              <th scope="col">Route</th>
              <th scope="col" className="num">Revenue</th>
              <th scope="col" className="num">Cost</th>
              <th scope="col" className="num">Profit</th>
              <th scope="col" className="num">Load factor</th>
              <th scope="col" className="num">Yield</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((row, index) => (
              <tr
                key={row.route.legId}
                className="rap-row"
                onClick={() => setOpenLegId(row.route.legId)}
              >
                <td className="num rap-muted">{index + 1}</td>
                <td>
                  <button type="button" className="rap-route">
                    {row.route.origin}–{row.route.destination}
                  </button>
                  <span className="rap-muted"> · {row.route.aircraftReg}</span>
                </td>
                <td className="num">{formatUsd(row.route.revenueUSD)}</td>
                <td className="num">{formatUsd(row.route.operatingCostUSD)}</td>
                <td className={`num ${row.profitUSD >= 0 ? 'pos' : 'neg'}`}>{formatUsd(row.profitUSD)}</td>
                <td className="num">{formatPct(row.route.loadFactorPct)}</td>
                <td className="num">{row.revenuePassengerMiles > 0 ? formatCents(row.yieldCents) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openRoute !== undefined && (
        <RouteDetailModal
          title={`${openRoute.origin}–${openRoute.destination}`}
          subtitle={`Leg ${openRoute.legId} · ${openRoute.aircraftReg}`}
          routes={[openRoute]}
          competitorShares={competitorShares}
          onClose={() => setOpenLegId(null)}
          onOpenInspector={(legId) => {
            setOpenLegId(null);
            onOpenInspector(legId);
          }}
        />
      )}
    </div>
  );
}
